import { fetchWithCSRF } from "@/helpers/common/csrf";
import clsx from "clsx";
import { ChevronLeft, ChevronRight, RefreshCw, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate, formatSeconds, shortName, StatusBadge } from "./_helpers";
import {
    BackgroundTask,
    BackgroundTaskPagination,
    BackgroundTaskStats,
} from "./_types";

const API_URL = import.meta.env.VITE_VANDERHEIM_NINJA_API_URL;
const BASE_URL = `${API_URL}/longhouse-api/village/background-tasks/`;
const SEARCH_DELAY_MS = 300;
const AUTO_REFRESH_MS = 15000;

const STATUSES = [
    "pending",
    "running",
    "completed",
    "failed",
    "retrying",
    "revoked",
    "lost",
] as const;

const emptyFilters = {
    search: "",
    status: "",
    queue: "",
    dateFrom: "",
    dateTo: "",
};

const inputClasses =
    "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100";

const pageButtonClasses = (enabled: boolean) =>
    clsx(
        "flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        enabled
            ? "bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-600",
    );

export default function BackgroundTasksPage() {
    const [filters, setFilters] = useState(emptyFilters);
    const [typedSearch, setTypedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [refreshCount, setRefreshCount] = useState(0);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [tasks, setTasks] = useState<BackgroundTask[]>([]);
    const [stats, setStats] = useState<BackgroundTaskStats | null>(null);
    const [pagination, setPagination] =
        useState<BackgroundTaskPagination | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    const updateFilters = (changes: Partial<typeof emptyFilters>) => {
        setFilters((current) => ({ ...current, ...changes }));
        setPage(1);
    };

    // Wait for a pause in typing so each keystroke doesn't run a search.
    useEffect(() => {
        if (filters.search === typedSearch) return;
        const timer = setTimeout(() => {
            setFilters((current) => ({ ...current, search: typedSearch }));
            setPage(1);
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [typedSearch, filters.search]);

    useEffect(() => {
        if (!autoRefresh) return;
        const timer = setInterval(
            () => setRefreshCount((count) => count + 1),
            AUTO_REFRESH_MS,
        );
        return () => clearInterval(timer);
    }, [autoRefresh]);

    useEffect(() => {
        const controller = new AbortController();
        const params = new URLSearchParams({
            page: String(page),
            page_size: "50",
        });
        if (filters.search) params.set("search", filters.search);
        if (filters.status) params.set("status", filters.status);
        if (filters.queue) params.set("queue", filters.queue);
        if (filters.dateFrom) {
            params.set("date_from", new Date(filters.dateFrom).toISOString());
        }
        if (filters.dateTo) {
            params.set("date_to", new Date(filters.dateTo).toISOString());
        }

        const load = (url: string) =>
            fetchWithCSRF(url, { signal: controller.signal }).then((res) => {
                if (!res.ok) throw new Error(`Status ${res.status}`);
                return res.json();
            });

        Promise.all([load(`${BASE_URL}?${params}`), load(`${BASE_URL}stats`)])
            .then(([list, latestStats]) => {
                setTasks(list.results);
                setPagination(list.pagination);
                setStats(latestStats);
                setFailed(false);
                setLoading(false);
            })
            .catch((error) => {
                if (controller.signal.aborted) return;
                console.error("Error fetching background tasks:", error);
                setFailed(true);
                setLoading(false);
            });

        return () => controller.abort();
    }, [filters, page, refreshCount]);

    const clearFilters = () => {
        setTypedSearch("");
        updateFilters(emptyFilters);
    };

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-8">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Background Tasks
                    </h1>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Every Celery task from the last 14 days.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                        />
                        Auto-refresh
                    </label>
                    <button
                        onClick={() => setRefreshCount((count) => count + 1)}
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </button>
                </div>
            </div>

            {stats && (
                <>
                    <div className="mb-3 grid gap-3 sm:grid-cols-3">
                        {stats.queues.map((queue) => (
                            <div
                                key={queue.queue || "unknown"}
                                className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
                            >
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {queue.queue || "Unknown"}
                                </p>
                                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                                    {queue.waiting} waiting · {queue.running}{" "}
                                    running
                                </p>
                                {queue.longest_wait_seconds !== null && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Longest wait{" "}
                                        {formatSeconds(
                                            queue.longest_wait_seconds,
                                        )}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="mb-6">
                        <p className="mb-2 text-xs text-slate-600 dark:text-slate-400">
                            Last 24 hours
                        </p>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                            {STATUSES.map((status) => (
                                <button
                                    key={status}
                                    onClick={() =>
                                        updateFilters({
                                            status:
                                                filters.status === status
                                                    ? ""
                                                    : status,
                                        })
                                    }
                                    className={clsx(
                                        "rounded-xl border bg-white p-3 text-left dark:bg-slate-800",
                                        filters.status === status
                                            ? "border-emerald-500"
                                            : "border-slate-200 dark:border-slate-700",
                                    )}
                                >
                                    <span className="block text-xs text-slate-600 capitalize dark:text-slate-400">
                                        {status}
                                    </span>
                                    <span className="text-xl font-bold text-slate-900 dark:text-slate-100">
                                        {stats.statuses[status]}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}

            <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <label className="relative">
                    <span className="sr-only">Search task names</span>
                    <Search className="absolute top-2.5 left-3 h-4 w-4 text-slate-400" />
                    <input
                        type="search"
                        placeholder="Search task names"
                        value={typedSearch}
                        onChange={(e) => setTypedSearch(e.target.value)}
                        className={clsx(inputClasses, "w-full pl-9")}
                    />
                </label>
                <label>
                    <span className="sr-only">Filter by status</span>
                    <select
                        value={filters.status}
                        onChange={(e) =>
                            updateFilters({ status: e.target.value })
                        }
                        className={clsx(inputClasses, "w-full capitalize")}
                    >
                        <option value="">All statuses</option>
                        {STATUSES.map((status) => (
                            <option key={status} value={status}>
                                {status}
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    <span className="sr-only">Filter by queue</span>
                    <select
                        value={filters.queue}
                        onChange={(e) =>
                            updateFilters({ queue: e.target.value })
                        }
                        className={clsx(inputClasses, "w-full")}
                    >
                        <option value="">All queues</option>
                        <option value="realtime">realtime</option>
                        <option value="vanderheim">vanderheim</option>
                        <option value="bulk">bulk</option>
                    </select>
                </label>
                <button
                    onClick={clearFilters}
                    className="self-end rounded-lg bg-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                >
                    Clear filters
                </button>
                <label className="text-xs text-slate-600 dark:text-slate-400">
                    From
                    <input
                        type="datetime-local"
                        value={filters.dateFrom}
                        onChange={(e) =>
                            updateFilters({ dateFrom: e.target.value })
                        }
                        className={clsx(inputClasses, "mt-1 w-full")}
                    />
                </label>
                <label className="text-xs text-slate-600 dark:text-slate-400">
                    To
                    <input
                        type="datetime-local"
                        value={filters.dateTo}
                        onChange={(e) =>
                            updateFilters({ dateTo: e.target.value })
                        }
                        className={clsx(inputClasses, "mt-1 w-full")}
                    />
                </label>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
                {failed ? (
                    <p className="p-8 text-center text-sm text-red-600 dark:text-red-400">
                        Background tasks could not be loaded.
                    </p>
                ) : tasks.length === 0 ? (
                    <p className="p-8 text-center text-sm text-slate-600 dark:text-slate-400">
                        {loading ? "Loading…" : "No tasks found."}
                    </p>
                ) : (
                    <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                        {tasks.map((task) => (
                            <li key={task.id}>
                                <Link
                                    to={String(task.id)}
                                    className="flex flex-col gap-1 px-4 py-3 hover:bg-slate-50 sm:flex-row sm:items-center sm:gap-4 dark:hover:bg-slate-700/50"
                                >
                                    <time
                                        dateTime={task.queued_at}
                                        className="shrink-0 font-mono text-xs text-slate-500 sm:w-44 dark:text-slate-400"
                                    >
                                        {formatDate(task.queued_at)}
                                    </time>
                                    <div className="min-w-0 flex-1 text-sm">
                                        <p
                                            title={task.name}
                                            className="truncate font-semibold text-slate-900 dark:text-slate-100"
                                        >
                                            {shortName(task.name)}
                                        </p>
                                        {task.error && (
                                            <p className="truncate text-xs text-red-600 dark:text-red-400">
                                                {task.error}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex shrink-0 flex-wrap items-center gap-3 text-xs text-slate-600 dark:text-slate-400">
                                        <span>{task.queue || "—"}</span>
                                        <span>
                                            Waited{" "}
                                            {formatSeconds(task.wait_seconds)}
                                        </span>
                                        <span>
                                            Ran{" "}
                                            {formatSeconds(
                                                task.duration_seconds,
                                            )}
                                        </span>
                                        <StatusBadge status={task.status} />
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}

                {pagination && pagination.total_items > 0 && (
                    <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Page {pagination.page} of {pagination.total_pages} (
                            {pagination.total_items} tasks)
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(page - 1)}
                                disabled={!pagination.has_previous_page}
                                className={pageButtonClasses(
                                    pagination.has_previous_page,
                                )}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(page + 1)}
                                disabled={!pagination.has_next_page}
                                className={pageButtonClasses(
                                    pagination.has_next_page,
                                )}
                            >
                                Next
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
